import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const ROUND_SECONDS = 60;
const SLOTS = 5;
const MAX_BREACHES = 4;

interface Message {
  sender: string;
  subject: string;
  phishing: boolean;
  flags: string[];
  age: number;
  lifetime: number;
}

const SAFE_SENDERS = ["it-helpdesk", "payroll", "benefits", "calendar", "team-updates"] as const;
const PHISH_SENDERS = ["1t-he1pdesk", "secure-payroll", "account-alert", "hr-urgent", "mail-admin"] as const;
const SAFE_SUBJECTS = ["Weekly team sync notes", "Your pay stub is ready", "Benefits enrollment window", "Meeting moved to 2pm", "Policy handbook update"] as const;
const PHISH_SUBJECTS = ["Verify your account NOW", "Unusual sign-in - act fast", "Payment on hold - confirm", "Password expires today", "Invoice overdue - pay here"] as const;
const PHISH_FLAGS = ["LOOKALIKE DOMAIN", "URGENT ASK", "LINK MISMATCH", "UNKNOWN SENDER", "CREDENTIAL BAIT"] as const;

export class PhishQuestGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0x9153);
  private readonly slots: (Message | null)[] = Array<Message | null>(SLOTS).fill(null);
  private focus = 0;
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private spawnTimer = 0.6;
  private moveCooldown = 0;
  private breaches = 0;
  private message = "Quarantine phishing before it lands. Deliver clean mail.";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {
    this.slots[0] = this.createMessage();
  }

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.moveCooldown = Math.max(0, this.moveCooldown - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Quarantine phishing before it lands. Deliver clean mail.";

    if (this.time === 0) {
      this.finish("Mailbox defended");
      return;
    }

    this.updateFocus(deltaSeconds);
    if (this.services.input.consumePressed("primary")) this.quarantineFocused();
    this.updateInbox(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    const background = context.createLinearGradient(0, 0, 1280, 720);
    background.addColorStop(0, "#0a1226");
    background.addColorStop(0.6, "#0c1c34");
    background.addColorStop(1, "#080d1c");
    context.fillStyle = background;
    context.fillRect(0, 0, 1280, 720);
    drawScanlines(context);

    this.drawSidebar(context);
    this.drawInbox(context);
    this.drawFooter(context);
  }

  getHud(): GameHud {
    return { score: this.score, time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {}

  private createMessage(): Message {
    const phishing = this.random.next() < 0.5;
    if (phishing) {
      const flagCount = 1 + this.random.integer(2);
      const flags: string[] = [];
      while (flags.length < flagCount) {
        const flag = PHISH_FLAGS[this.random.integer(PHISH_FLAGS.length)] ?? "URGENT ASK";
        if (!flags.includes(flag)) flags.push(flag);
      }
      return {
        sender: `${PHISH_SENDERS[this.random.integer(PHISH_SENDERS.length)] ?? "mail-admin"}@corp-secure.co`,
        subject: PHISH_SUBJECTS[this.random.integer(PHISH_SUBJECTS.length)] ?? "Verify your account NOW",
        phishing: true,
        flags,
        age: 0,
        lifetime: this.random.between(4.4, 6.4),
      };
    }
    return {
      sender: `${SAFE_SENDERS[this.random.integer(SAFE_SENDERS.length)] ?? "team-updates"}@enigmastack.com`,
      subject: SAFE_SUBJECTS[this.random.integer(SAFE_SUBJECTS.length)] ?? "Weekly team sync notes",
      phishing: false,
      flags: ["SIGNED", "KNOWN SENDER"],
      age: 0,
      lifetime: this.random.between(4.8, 6.8),
    };
  }

  private updateFocus(deltaSeconds: number): void {
    const input = this.services.input;
    let direction = 0;
    if (input.consumePressed("down") || input.consumePressed("right")) direction = 1;
    else if (input.consumePressed("up") || input.consumePressed("left")) direction = -1;
    else if (this.moveCooldown === 0) {
      if (input.isDown("down") || input.isDown("right")) direction = 1;
      else if (input.isDown("up") || input.isDown("left")) direction = -1;
    }
    if (direction === 0) return;
    void deltaSeconds;
    this.focus = (this.focus + direction + SLOTS) % SLOTS;
    this.moveCooldown = 0.16;
    this.services.audio.play("move");
  }

  private quarantineFocused(): void {
    const message = this.slots[this.focus];
    if (!message) return;
    this.slots[this.focus] = null;
    if (message.phishing) {
      this.score += 180;
      this.services.audio.play("hit");
      this.setMessage("Threat quarantined", 1.4);
    } else {
      this.registerBreach("False positive - clean mail quarantined");
    }
  }

  private updateInbox(deltaSeconds: number): void {
    this.spawnTimer -= deltaSeconds;
    if (this.spawnTimer <= 0) {
      const free = this.slots.indexOf(null);
      if (free >= 0) this.slots[free] = this.createMessage();
      this.spawnTimer = Math.max(0.7, 1.5 - this.elapsed * 0.012) + this.random.between(0, 0.4);
    }

    for (let index = 0; index < SLOTS; index += 1) {
      const message = this.slots[index];
      if (!message) continue;
      message.age += deltaSeconds;
      if (message.age < message.lifetime) continue;
      this.slots[index] = null;
      if (message.phishing) {
        this.registerBreach("Phishing message delivered");
      } else {
        this.score += 90;
        this.services.audio.play("confirm");
        this.setMessage("Clean mail delivered", 1.1);
      }
    }
  }

  private registerBreach(reason: string): void {
    this.breaches += 1;
    this.score = Math.max(0, this.score - 60);
    this.services.audio.play("miss");
    if (this.breaches >= MAX_BREACHES) {
      this.finish("Inbox compromised");
      return;
    }
    this.setMessage(`${reason} - ${MAX_BREACHES - this.breaches} left`, 1.8);
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${this.score}.`);
  }

  private drawSidebar(context: CanvasRenderingContext2D): void {
    drawPanel(context, 56, 78, 360, 470, "#42e2c3");
    setText(context, 14, 850, "#9feddd");
    context.fillText("EMAIL SECURITY", 92, 116);
    setText(context, 40, 900, "#f7fbff");
    context.fillText("Triage", 92, 164);
    setText(context, 16, 700, "#a9bbc4");
    context.fillText("Quarantine phishing with SPACE.", 92, 210);
    context.fillText("Leave clean mail to deliver.", 92, 236);

    setText(context, 12, 850, "#839aa5");
    context.fillText("RED FLAGS", 92, 300);
    const legend = ["LOOKALIKE DOMAIN", "URGENT ASK", "LINK MISMATCH", "CREDENTIAL BAIT"];
    for (let index = 0; index < legend.length; index += 1) {
      const label = legend[index];
      if (!label) continue;
      context.fillStyle = "rgba(255, 93, 93, 0.16)";
      roundedRectPath(context, 92, 320 + index * 40, 280, 30, 8);
      context.fill();
      setText(context, 13, 800, "#ff9c9c");
      context.fillText(label, 106, 335 + index * 40);
    }

    setText(context, 12, 850, "#839aa5");
    context.fillText("INBOX INTEGRITY", 92, 500);
    for (let index = 0; index < MAX_BREACHES; index += 1) {
      const x = 232 + index * 38;
      context.fillStyle = index < MAX_BREACHES - this.breaches ? "#42e2c3" : "#3c4651";
      context.beginPath();
      context.arc(x, 496, 11, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawInbox(context: CanvasRenderingContext2D): void {
    const x = 456;
    const width = 768;
    const rowHeight = 84;
    const top = 86;
    setText(context, 12, 850, "#839aa5");
    context.fillText("INCOMING", x + 8, top - 4);

    for (let index = 0; index < SLOTS; index += 1) {
      const y = top + index * (rowHeight + 8);
      const message = this.slots[index];
      const focused = index === this.focus;
      context.save();
      roundedRectPath(context, x, y, width, rowHeight, 14);
      context.fillStyle = focused ? "rgba(66, 226, 195, 0.12)" : "rgba(6, 14, 24, 0.7)";
      context.fill();
      context.strokeStyle = focused ? "#42e2c3" : "rgba(255, 255, 255, 0.12)";
      context.lineWidth = focused ? 3 : 1.5;
      context.stroke();

      if (message) {
        setText(context, 18, 800, "#f7fbff");
        context.fillText(message.subject, x + 24, y + 30);
        setText(context, 13, 700, "#9cb2bd");
        context.fillText(message.sender, x + 24, y + 56);

        let tagX = x + 24;
        for (const flag of message.flags) {
          const safe = flag === "SIGNED" || flag === "KNOWN SENDER";
          setText(context, 11, 800, safe ? "#9feddd" : "#ff9c9c");
          const tagWidth = flag.length * 7.4 + 20;
          context.fillStyle = safe ? "rgba(66, 226, 195, 0.14)" : "rgba(255, 93, 93, 0.16)";
          roundedRectPath(context, tagX, y + 62, tagWidth, 16, 8);
          context.fill();
          context.fillStyle = safe ? "#9feddd" : "#ff9c9c";
          context.fillText(flag, tagX + 10, y + 70);
          tagX += tagWidth + 8;
        }

        const remaining = Math.max(0, 1 - message.age / message.lifetime);
        context.fillStyle = "rgba(255, 255, 255, 0.1)";
        roundedRectPath(context, x + width - 150, y + 22, 120, 10, 5);
        context.fill();
        context.fillStyle = message.phishing ? "#ff5d5d" : "#42e2c3";
        roundedRectPath(context, x + width - 150, y + 22, Math.max(3, 120 * remaining), 10, 5);
        context.fill();
      } else {
        setText(context, 14, 700, "#48586a");
        context.fillText("— empty —", x + 24, y + rowHeight / 2);
      }
      context.restore();
    }
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 56, 578, 1168, 88, "#42e2c3");
    setText(context, 12, 850, "#9feddd");
    context.fillText("OBJECTIVE", 92, 608);
    setText(context, 24, 850, "#f7fbff");
    context.fillText(this.message, 92, 640);
    setText(context, 13, 750, "#9cb2bd", "right");
    context.fillText("NAVIGATE  \u2191 \u2193     QUARANTINE  SPACE", 1188, 640);
  }
}
